export type ClinicalPathwayCode =
  | 'SEPSIS_RECOGNITION'
  | 'FALLS_POST_EVENT'
  | 'DELIRIUM_SCREENING'
  | 'END_OF_LIFE'
  | 'WOUND_HEALING';

export type ClinicalPathwayStatus = 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'EXITED' | 'DRAFT';

export type ClinicalPathwayStepLike = {
  id: string;
  stepCode: string;
  name: string;
  stepOrder: number;
  required: boolean;
  slaHours: number | null;
  dueAt?: string | null;
  completedAt?: string | null;
};

export type ClinicalPathwayProgress = {
  completedRequired: number;
  totalRequired: number;
  percentComplete: number;
  nextStep: ClinicalPathwayStepLike | null;
  overdueSteps: ClinicalPathwayStepLike[];
};

export const CLINICAL_PATHWAY_LABELS: Record<ClinicalPathwayCode, string> = {
  SEPSIS_RECOGNITION: 'Sepsis recognition',
  FALLS_POST_EVENT: 'Falls post-event',
  DELIRIUM_SCREENING: 'Delirium screening',
  END_OF_LIFE: 'End of life',
  WOUND_HEALING: 'Wound healing',
};

export const RESTORATIVE_PROGRAMME_LABELS = {
  RESTORATIVE_CARE: 'Restorative care',
  REABLEMENT: 'Reablement',
  REHABILITATION: 'Rehabilitation',
  MAINTENANCE: 'Maintenance',
} as const;

export type RestorativeProgrammeKind = keyof typeof RESTORATIVE_PROGRAMME_LABELS;

export function formatClinicalPathwayCode(code: string): string {
  return CLINICAL_PATHWAY_LABELS[code as ClinicalPathwayCode] ?? formatClinicalToken(code);
}

export function formatClinicalToken(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function calculateClinicalPathwayProgress(
  steps: ClinicalPathwayStepLike[],
  now: Date = new Date(),
): ClinicalPathwayProgress {
  const orderedSteps = [...steps].sort((left, right) => left.stepOrder - right.stepOrder);
  const requiredSteps = orderedSteps.filter((step) => step.required);
  const completedRequired = requiredSteps.filter((step) => Boolean(step.completedAt)).length;
  const requiredStepCount = requiredSteps.length;
  const percentComplete =
    requiredStepCount === 0 ? 100 : Math.round((completedRequired / requiredStepCount) * 100);
  const nextStep = orderedSteps.find((step) => !step.completedAt) ?? null;
  const overdueSteps = orderedSteps.filter(
    (step) => !step.completedAt && step.dueAt && new Date(step.dueAt).getTime() < now.getTime(),
  );

  return {
    completedRequired,
    totalRequired: requiredStepCount,
    percentComplete,
    nextStep,
    overdueSteps,
  };
}

export function pathwayRequiresCurrentDirective(code: string): boolean {
  return code === 'END_OF_LIFE';
}
